<template>
    <x-page title="Group">
        <template #header-actions>
            <x-create-button url="/admin/groups/create" 
                :disabled="isLoading"
                v-if="can('createGroups')" />
        </template>

        <x-filter-box :onFilter="() => handleFilter()"
            :onReset="resetFilter">
            <div class="row">
                <div class="col-xs-12 col-md-6">
                    <x-form-group label="Label">
                        <input class="form-control"
                            type="text"
                            v-model="filters.search"
                            @keyup.enter="() => handleFilter()"
                            placeholder="Masukkan label group" />
                    </x-form-group>
                </div>
                <div class="col-xs-12 col-md-6">
                    <x-form-group label="Kategori">
                        <x-ajax-dropdown url="/api/group-categories"
                            label="label"
                            :allow-empty="true"
                            v-model="selectedCategory"
                            :query="{ without_global_scopes: 1 }"
                            class="form-control"
                            placeholder="Ketik label kategori" />
                    </x-form-group>
                </div>
            </div>
        </x-filter-box>

        <x-excel-import-modal v-if="can('updateGroups')"
            :action="`/admin/groups/${highlightedGroup ? highlightedGroup.id : null}/member-import`"
            id="import-member-modal"
            :template-url="`/admin/groups/${highlightedGroup ? highlightedGroup.id : null}/member-export?empty=1`">
            <x-form-group label="Enable Delete"
                help-text="Data dengan ID yang tidak terdapat dalam file excel akan dihapus">
                <x-checkbox-wrapper label="Hapus data yang tidak ada dalam excel">
                    <input type="checkbox"
                        name="overwrite"
                        value="1" />
                </x-checkbox-wrapper>
            </x-form-group>
        </x-excel-import-modal>

        <x-table :headers="[
            '#',
            'Label',
            'Kategori',
            'Leader',
            'Action',
        ]" :is-loading="isLoading">
            <tr v-for="(resource, index) in resources" :key="index">
                <td>{{ rowIndex(index, $route.query) }}</td>
                <td class="nowrap">
                    <router-link :to="`/admin/groups/${resource.id}`" 
                        title="Klik untuk lihat detail">
                        {{ resource.label }}
                    </router-link>

                    <div>
                        <!-- SMS blast label -->
                        <x-sms-blast-label 
                            :inline="true"
                            title="Setting dapat diubah di kategori" 
                            v-if="resource.category && resource.category.sms_birthday_blast"
                        />

                        <!-- Enter trigger label -->
                        <x-label-group-trigger 
                            v-if="resource.enter_trigger" 
                            color="success"
                            label="Trigger masuk"
                            title="Trigger masuk aktif"
                        />

                        <!-- Exit trigger label -->
                        <x-label-group-trigger 
                            v-if="resource.exit_trigger" 
                            color="danger"
                            label="Trigger keluar"
                            title="Trigger keluar aktif"
                        />
                    </div>
                </td>
                <td class="nowrap">
                    {{ resource | get('category.label', '-') }}

                    <!-- Category trigger label -->
                    <div v-if="resource.category">
                        <!-- Enter trigger label -->
                        <x-label-group-trigger 
                            v-if="hasEnterTrigger(resource.category)" 
                            color="success"
                            label="Trigger masuk"
                            title="Trigger masuk aktif"
                        />

                        <!-- Exit trigger label -->
                        <x-label-group-trigger 
                            v-if="hasExitTrigger(resource.category)"  
                            color="danger"
                            label="Trigger keluar"
                            title="Trigger keluar aktif"
                        />
                    </div>
                </td>
                <td class="nowrap">
                    {{ resource | get('leader.name', '-') }}
                </td>
                <td>
                    <x-quick-action-button :url="`/admin/gatherings?group_id=${resource.id}`"
                        icon="calendar-check-o"
                        title="Absensi"
                        :disabled="isLoading"
                        v-if="canViewGroupGatherings(resource.id)" />
                    <x-quick-action-button :href="`/admin/groups/${resource.id}/member-export`"
                        icon="cloud-download"
                        title="Export Anggota"
                        :disabled="isLoading" />
                    <x-quick-action-button icon="cloud-upload"
                        title="Import Anggota"
                        :disabled="isLoading"
                        @click="showMemberImport(resource)"
                        v-if="canUpdateGroup(resource.id)" />
                    <x-quick-edit-button :url="`/admin/groups/${resource.id}/edit`"
                        :disabled="isLoading" v-if="canUpdateGroup(resource.id)" />
                    <x-quick-delete-button @click="deleteResource(resource)"
                        :disabled="isLoading" v-if="can('deleteGroups')" />
                </td>
            </tr>
        </x-table>

        <x-pagination v-if="shouldRenderPagination" :pagination="meta" :query="$route.query" />
    </x-page>
</template>

<script>
import XActionButton from '../../components/XActionButton.vue'
import XAjaxDropdown from '../../components/XAjaxDropdown.vue'
import XCheckboxWrapper from '../../components/XCheckboxWrapper.vue'
import XCreateButton from '../../components/XCreateButton.vue'
import XExcelImportModal from '../../components/XExcelImportModal.vue'
import XFilterBox from '../../components/XFilterBox.vue'
import XFormGroup from '../../components/XFormGroup.vue'
import XLabelGroupTrigger from '../../components/XLabelGroupTrigger.vue'
import XPage from '../../components/XPage.vue'
import XPagination from '../../components/XPagination.vue'
import XQuickActionButton from '../../components/XQuickActionButton.vue'
import XQuickDeleteButton from '../../components/XQuickDeleteButton.vue'
import XQuickEditButton from '../../components/XQuickEditButton.vue'
import XQuickViewButton from '../../components/XQuickViewButton.vue'
import XSmsBlastLabel from '../../components/XSmsBlastLabel.vue'
import XTable from '../../components/XTable.vue'
import { deletePrompt, getAllowedGroupIds, handleGeneralXhrError, hasRestrictedGroupViewPermission, setGroupListUrl } from '../../helpers'
import FilterableMixin from '../../mixins/FilterableMixin'
import PaginationMixin from '../../mixins/PaginationMixin'
import PermissionMixin from '../../mixins/PermissionMixin'
import TriggerableMixin from '../../mixins/TriggerableMixin'

const defaultFilters = {
    category_id: null,
    ids: hasRestrictedGroupViewPermission() ? getAllowedGroupIds() : null,
    search: null,
}

export default {
    beforeRouteUpdate(to, from, next) {
        if (this.hasInvalidQuery(to)) {
            next(false)
        } else {
            setGroupListUrl(to.fullPath)
            this.fetchResources(to)
		    next()
        }
	},
    components: {
        XCreateButton,
        XTable,
        XPage,
        XFormGroup,
        XFilterBox,
        XAjaxDropdown,
        XActionButton,
        XExcelImportModal,
        XCheckboxWrapper,
        XPagination,
        XQuickActionButton,
        XQuickViewButton,
        XQuickEditButton,
        XQuickDeleteButton,
        XSmsBlastLabel,
        XLabelGroupTrigger,
    },
    created() {
        this.preselectFilters(defaultFilters)
        this.handleFilter(this.$route.query.page || 1).catch(() => {
            this.fetchResources()
        })
    },
    data() {
        return {
            filters: { ...defaultFilters },
            highlightedGroup: null,
            isLoading: false,
            resources: [],
            selectedCategory: null,
        }
    },
    methods: {
        deleteResource(resource) {
            deletePrompt({
                name: resource.label,
                onConfirm: () => {
                    this.isLoading = true
                    const request = axios.delete(`/api/groups/${resource.id}`)
                        .then(() => {
                            this.$notify({
                                type: 'success',
                                title: `Berhasil`,
                                text: `${resource.label} dihapus!`,
                            })
                            this.fetchResources()
                        })
                    handleGeneralXhrError(request).finally(() => {
                        this.isLoading = false
                    })
                },
            })
        },
        fetchResources(route = this.$route) {
            this.isLoading = true
            const request = axios.get('/api/groups', {
                params: {
                    ...route.query,
                    append: [
                        'enter_trigger',
                        'exit_trigger',
                        'leader',
                    ],
                    with: [
                        'category.triggers',
                    ],
                    without_global_scopes: 1,
                },
            })
            .then(response => {
                this.resources = response.data.data
                this.setMeta(response)
            })
            return handleGeneralXhrError(request).finally(() => {
                this.isLoading = false
            })
        },
        handleFilter(page = 1) {
            return this.$router.push({
                path: this.$route.path,
                query: _.pickBy({
                    ...this.filters,
                    page,
                }),
            })
        },
        resetFilter() {
            this.filters = { ...defaultFilters }
            this.handleFilter()
        },
        hasInvalidQuery(destinationRoute) {
            return (hasRestrictedGroupViewPermission()
                && !Boolean(_.get(destinationRoute, 'query.ids')))
        },
        showMemberImport(group) {
            this.highlightedGroup = group
            $('#import-member-modal').modal('show')
        },
    },
    mixins: [
        FilterableMixin,
        PaginationMixin,
        PermissionMixin,
        TriggerableMixin,
    ],
    watch: {
        'filters.category_id': {
            handler(id) {
                this.syncFilterToProperty(id, 'selectedCategory', `/api/group-categories/${id}`)
            },
            immediate: true,
        },
        selectedCategory: {
            handler(resource) {
                this.syncPropertyToFilter(resource, 'category_id')
            },
            deep: true,
        },
    }
}
</script>